import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { IntermediaryEntity } from '../services/api.bokkz.services';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ToastService } from '@snelstart/ui-toast';
import { BokkzAlgDataService } from '../services/bokkz-alg-data-service';
import { BokkzAlgLogic } from '../services/bokkz-alg-logic';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { CommonModule } from '@angular/common';
import { SharedUiStepperModule, StepperItem } from '@snelstart/ui-stepper';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { Subject, takeUntil } from 'rxjs';
import { SharedUiLoadingModule } from '@snelstart/ui-loading';

@Component({
    selector: 'snelstart-venture-fiscaal-domain-logic-upload-clients',
    templateUrl: './upload-clients.component.html',
    imports: [
        SharedUiDialogModule,
        SharedUiButtonModule,
        ReactiveFormsModule,
        SharedUiDetailsModule,
        CommonModule,
        SharedUiStepperModule,
        SharedUiFormControlsModule,
        SharedUiAlertModule,
        SharedUiLoadingModule,
    ],
    styleUrl: './upload-clients.component.css',
})
export class UploadClientsComponent implements OnInit, OnDestroy {
    @Input() public dialogVisible = false;
    @Output() public dialogVisibleChange = new EventEmitter<boolean>();

    private destroy$ = new Subject<void>();
    public clientId = {} as number;
    public intermediaryEntity = {} as IntermediaryEntity;
    public fileName = '';
    public message = '';
    public loading = false;
    public uploadFg = {} as FormGroup;
    public items = [] as StepperItem[];
    public activeIndex = 0;
    public optionsPakketten: SelectFormControlItem<string>[] = [];
    protected hasError = false;
    public error = {} as string;
    public uploadStepLabel = 'Upload Excel';

    constructor(
        private algLogic: BokkzAlgLogic,
        private algDataService: BokkzAlgDataService,
        public toastService: ToastService,
        public fb: FormBuilder,
    ) {
        this.uploadFg = this.fb.group({
            name: null as unknown as string,
            file: null as unknown as string,
            attachments: this.fb.array([]),
        });
        this.optionsPakketten.push(
            { value: 'Exact Fiscaal Online', label: 'Exact Fiscaal Online' },
            { value: 'Nextens', label: 'Nextens' },
            { value: 'Fiscaal Online', label: 'Fiscaal Online', disabled: true },
            { value: 'Technow', label: 'Technow', disabled: true },
            { value: 'Afas Fiscaal', label: 'Afas Fiscaal', disabled: true },
        );
        this.items = [
            {
                label: 'Selecteer huidige pakket',
                state: 'active',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: this.uploadStepLabel,
                state: 'toDo',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Verwerken',
                state: 'toDo',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Upload Personen.csv',
                state: 'toDo',
                condition: () => this.uploadFg.get('name')?.getRawValue() === 'Exact Fiscaal Online',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Verwerken',
                state: 'toDo',
                condition: () => this.uploadFg.get('name')?.getRawValue() === 'Exact Fiscaal Online',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Afsluiten',
                state: 'toDo',
                onClick: () => this.handleStepClick.bind(this),
            },
        ];
    }

    ngOnInit() {
        this.algDataService.intermediary.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.intermediaryEntity = value;
        });
        this.uploadFg.get('name')?.valueChanges.subscribe((value) => {
            this.updateUploadLabel(value);
        });
    }

    private updateUploadLabel(selectedPakket: string) {
        if (this.items[1]) {
            this.items[1].label = selectedPakket === 'Exact Fiscaal Online' ? 'Upload Ondernemingen.csv' : 'Upload Excel';
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onFileSelected(event: Event) {
        this.loading = true;
        const formData = new FormData();
        if (event.target) {
            const target = event.target as HTMLInputElement;
            if (target.files) {
                const file: File = target.files[0];
                if (file) {
                    this.fileName = file.name;
                    formData.append('file', file);
                }
            }
            this.algLogic.postClientFile(formData).subscribe({
                next: (response) => {
                    this.message = 'Er zijn ' + response + ' klanten succesvol ingelezen.';
                    this.loading = false;
                    setTimeout(() => {
                        this.handleStepClick();
                    }, 2000);
                },
                error: (err) => {
                    this.loading = false;
                    if (err.status === 504) {
                        this.message = 'Upload gestart. De verwerking gebeurt op de achtergrond.';
                        setTimeout(() => this.handleStepClick(), 2000);
                        return;
                    }
                    console.error('Upload failed:', err);
                    this.handleStepError(err);
                },
            });
        }
    }

    get activeItem() {
        return this.items.find((item) => item.state === 'active');
    }

    resetDialog(): void {
        this.uploadFg.reset();

        this.items = [
            {
                label: 'Selecteer huidige pakket',
                state: 'active',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Upload Excel',
                state: 'toDo',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Verwerken',
                state: 'toDo',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Upload ondernemingen',
                state: 'toDo',
                condition: () => this.uploadFg.get('name')?.getRawValue() === 'Exact Fiscaal Online',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Verwerken',
                state: 'toDo',
                condition: () => this.uploadFg.get('name')?.getRawValue() === 'Exact Fiscaal Online',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Afsluiten',
                state: 'toDo',
                onClick: () => this.handleStepClick.bind(this),
            },
        ];

        this.activeIndex = 0;

        // Close dialog
        this.dialogVisibleChange.next(false);
    }

    handleStepClick(): boolean {
        this.items[this.activeIndex].state = 'done';
        this.activeIndex++;

        if (this.uploadFg.get('name')?.getRawValue() === 'Nextens') {
            if (this.activeIndex === 3 || this.activeIndex === 4) {
                this.activeIndex = 5;
            }
        }
        this.items[this.activeIndex].state = 'active';
        return true;
    }
    handleStepError(error: any) {
        if (error.error) {
            this.error = JSON.stringify(error.error);
        } else if (error.message) {
            this.error = JSON.stringify(error.message);
        } else {
            this.error = 'Er is iets misgegaan met het verwerken van de klantenlijst.';
        }
        this.hasError = true;
        this.uploadFg.reset();
        this.items = [
            {
                label: 'Selecteer huidige pakket',
                state: 'active',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Upload Excel',
                state: 'toDo',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Verwerken',
                state: 'toDo',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Upload ondernemingen',
                state: 'toDo',
                condition: () => this.uploadFg.get('name')?.getRawValue() === 'Exact Fiscaal Online',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Verwerken',
                state: 'toDo',
                condition: () => this.uploadFg.get('name')?.getRawValue() === 'Exact Fiscaal Online',
                onClick: () => this.handleStepClick.bind(this),
            },
            {
                label: 'Afsluiten',
                state: 'toDo',
                onClick: () => this.handleStepClick.bind(this),
            },
        ];

        this.activeIndex = 0;
    }
}
